import type { Host } from '@fleet/shared';
import { hostView, plural, SERVERS } from '../format';
import { useApp, useTheme, type FilterKey, type Layout } from '../store';
import { btn, mono, panel, pill, pillGroup } from '../ui';

interface Bucket {
  key: string;
  label: string;
  range: string;
  color: string;
  hosts: Host[];
}

const BUCKET_DEFS: Array<[string, string, string, string, (h: Host) => boolean]> = [
  ['crit', 'Критическая загрузка', '90–100%', 'var(--bad)', h => h.cpu >= 90 && h.state !== 'offline'],
  ['high', 'Высокая загрузка', '70–89%', 'var(--warn)', h => h.cpu >= 70 && h.cpu < 90 && h.state !== 'offline'],
  ['norm', 'Рабочая', '30–69%', 'var(--acc)', h => h.cpu >= 30 && h.cpu < 70 && h.state !== 'offline'],
  ['idle', 'Простой', '0–29%', 'var(--ink4)', h => h.cpu < 30 && h.state !== 'offline'],
  ['off', 'Нет связи', 'агент офлайн', 'var(--ink4)', h => h.state === 'offline']
];

const FILTERS: Array<[FilterKey, string]> = [
  ['all', 'Все'],
  ['warn', 'Внимание'],
  ['hot', 'Горячие'],
  ['offline', 'Офлайн'],
  ['sel', 'Выбранные']
];

const LAYOUTS: Array<[Layout, string]> = [
  ['cards', 'Карточки'],
  ['table', 'Таблица'],
  ['compact', 'Сетка']
];

export function Fleet() {
  const app = useApp();

  const buckets: Bucket[] = BUCKET_DEFS.map(([key, label, range, color, test]) => ({
    key,
    label,
    range,
    color,
    hosts: app.shown.filter(test)
  })).filter(b => b.hosts.length);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--pad)' }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        {FILTERS.map(([key, label]) => {
          const on = app.filter === key;
          return (
            <button
              key={key}
              onClick={() => app.setFilter(key)}
              style={{
                ...btn,
                padding: '7px 13px',
                borderRadius: 99,
                fontSize: 12,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                border: `1px solid ${on ? 'var(--acc)' : 'var(--line)'}`,
                background: on ? 'var(--accSoft)' : 'transparent',
                color: on ? 'var(--ink)' : 'var(--ink3)'
              }}
            >
              {label}
            </button>
          );
        })}

        <span style={{ flex: 1 }} />

        <div style={pillGroup}>
          {LAYOUTS.map(([key, label]) => (
            <button key={key} onClick={() => app.setLayout(key)} style={pill(app.layout === key)}>
              {label}
            </button>
          ))}
        </div>

        <button
          className="hov"
          onClick={() => app.selectIds(app.shown.map(h => h.id))}
          style={{
            ...btn,
            padding: '7px 13px',
            borderRadius: 'var(--rs)',
            border: '1px solid var(--line)',
            fontSize: 12,
            whiteSpace: 'nowrap',
            color: 'var(--ink2)'
          }}
        >
          Выбрать всё · {app.shown.length}
        </button>
      </div>

      {buckets.map(b => (
        <section key={b.key} style={{ ...panel, overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
              padding: '14px 18px',
              borderBottom: '1px solid var(--line)'
            }}
          >
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: b.color, flex: 'none' }} />
            <span style={{ fontWeight: 600, fontSize: 14 }}>{b.label}</span>
            <span style={{ ...mono, fontSize: 11.5, color: 'var(--ink3)' }}>
              {b.range} · {plural(b.hosts.length, SERVERS)}
            </span>
            <span style={{ flex: 1 }} />
            <button
              className="hovAcc"
              onClick={() => app.selectIds(b.hosts.map(h => h.id))}
              style={{
                ...btn,
                padding: '6px 12px',
                borderRadius: 'var(--rs)',
                fontSize: 11.5,
                color: 'var(--acc)',
                whiteSpace: 'nowrap'
              }}
            >
              Выбрать группу
            </button>
            <button
              className="hov"
              onClick={() => {
                app.selectIds(b.hosts.map(h => h.id));
                app.go('commands');
              }}
              style={{
                ...btn,
                padding: '6px 12px',
                borderRadius: 'var(--rs)',
                border: '1px solid var(--line)',
                fontSize: 11.5,
                whiteSpace: 'nowrap',
                color: 'var(--ink2)'
              }}
            >
              Команда на группу
            </button>
          </div>

          {app.layout === 'cards' && <Cards hosts={b.hosts} />}
          {app.layout === 'table' && <Table hosts={b.hosts} />}
          {app.layout === 'compact' && <Compact hosts={b.hosts} />}
        </section>
      ))}

      {!buckets.length && (
        <section style={{ ...panel, padding: '40px 20px', fontSize: 12.5, color: 'var(--ink3)' }}>
          Под фильтр ничего не попало. Сбросьте поиск или выберите «Все».
        </section>
      )}
    </div>
  );
}

function Cards({ hosts }: { hosts: Host[] }) {
  const app = useApp();
  const theme = useTheme();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill,minmax(268px,1fr))',
        gap: 12,
        padding: '16px 18px'
      }}
    >
      {hosts.map(h => {
        const v = hostView(h, theme, !!app.sel[h.id]);
        return (
          <button
            key={h.id}
            className="hov2"
            onClick={() => app.toggleHost(h.id)}
            onDoubleClick={() => app.openHost(h.id)}
            title="Клик — выбрать, двойной клик — открыть карточку"
            style={{
              ...btn,
              boxSizing: 'border-box',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: '15px 16px',
              borderRadius: 'var(--r)',
              background: 'var(--panel2)',
              border: `1px solid ${v.cardBd}`
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%' }}>
              <span
                style={{ width: 7, height: 7, borderRadius: '50%', flex: 'none', background: v.stateColor }}
              />
              <span style={{ ...mono, fontSize: 12.5, fontWeight: 500 }}>{h.name}</span>
              <span style={{ flex: 1 }} />
              {v.selected && (
                <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--acc)' }}>выбран</span>
              )}
            </span>

            <span
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                ...mono,
                fontSize: 11,
                color: 'var(--ink3)',
                width: '100%'
              }}
            >
              <span>{h.ip}</span>
              <span>
                {h.role} · {h.loc}
              </span>
            </span>

            <span style={{ display: 'flex', gap: 14, width: '100%' }}>
              <Meter label="CPU" value={v.cpuStr} width={v.cpuW} color={v.loadColor} />
              <Meter label="RAM" value={v.ramStr} width={v.ramW} color="var(--ink3)" />
            </span>

            <span
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 11,
                borderTop: '1px solid var(--line)',
                ...mono,
                fontSize: 11,
                width: '100%'
              }}
            >
              <span style={{ color: v.tempColor }}>{v.tempStr}</span>
              <span style={{ color: 'var(--ink3)' }}>↓ {v.downStr}</span>
              <span style={{ color: 'var(--ink3)' }}>C: {v.diskStr}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Meter({ label, value, width, color }: { label: string; value: string; width: string; color: string }) {
  return (
    <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--ink3)' }}>
        <span>{label}</span>
        <span style={{ ...mono, color: 'var(--ink)' }}>{value}</span>
      </span>
      <span style={{ display: 'block', height: 5, borderRadius: 99, background: 'var(--track)' }}>
        <span style={{ display: 'block', height: 5, borderRadius: 99, background: color, width }} />
      </span>
    </span>
  );
}

const GRID = '22px 1.5fr 1fr .9fr 60px 120px 70px 70px 90px';

function Table({ hosts }: { hosts: Host[] }) {
  const app = useApp();
  const theme = useTheme();
  return (
    <div style={{ padding: '6px 18px 14px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: GRID,
          gap: 12,
          padding: '10px 0',
          fontSize: 11,
          color: 'var(--ink3)',
          borderBottom: '1px solid var(--line)'
        }}
      >
        <span />
        <span>Хост</span>
        <span>IP</span>
        <span>Роль</span>
        <span style={{ textAlign: 'right' }}>CPU</span>
        <span>Загрузка</span>
        <span style={{ textAlign: 'right' }}>Темп.</span>
        <span style={{ textAlign: 'right' }}>RAM</span>
        <span style={{ textAlign: 'right' }}>Канал</span>
      </div>
      {hosts.map(h => {
        const v = hostView(h, theme, !!app.sel[h.id]);
        return (
          <button
            key={h.id}
            className="hov"
            onClick={() => app.toggleHost(h.id)}
            onDoubleClick={() => app.openHost(h.id)}
            style={{
              ...btn,
              boxSizing: 'border-box',
              width: '100%',
              display: 'grid',
              gridTemplateColumns: GRID,
              gap: 12,
              alignItems: 'center',
              padding: '9px 0',
              borderBottom: '1px solid var(--line)',
              fontSize: 12
            }}
          >
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 4,
                border: `1px solid ${v.boxBd}`,
                background: v.boxBg
              }}
            />
            <span style={mono}>{h.name}</span>
            <span style={{ ...mono, color: 'var(--ink3)' }}>{h.ip}</span>
            <span style={{ color: 'var(--ink2)' }}>
              {h.role} · {h.loc}
            </span>
            <span style={{ textAlign: 'right', ...mono }}>{v.cpuStr}</span>
            <span style={{ display: 'block', height: 5, borderRadius: 99, background: 'var(--track)' }}>
              <span
                style={{ display: 'block', height: 5, borderRadius: 99, background: v.loadColor, width: v.cpuW }}
              />
            </span>
            <span style={{ textAlign: 'right', ...mono, color: v.tempColor }}>{v.tempStr}</span>
            <span style={{ textAlign: 'right', ...mono, color: 'var(--ink3)' }}>{v.ramStr}</span>
            <span style={{ textAlign: 'right', ...mono, color: 'var(--ink3)' }}>{v.downStr}</span>
          </button>
        );
      })}
    </div>
  );
}

function Compact({ hosts }: { hosts: Host[] }) {
  const app = useApp();
  const theme = useTheme();
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '16px 18px' }}>
      {hosts.map(h => {
        const v = hostView(h, theme, !!app.sel[h.id]);
        return (
          <button
            key={h.id}
            className="lift"
            title={v.tip}
            onClick={() => app.toggleHost(h.id)}
            onDoubleClick={() => app.openHost(h.id)}
            style={{
              ...btn,
              position: 'relative',
              width: 40,
              height: 40,
              borderRadius: 'var(--rs)',
              boxSizing: 'border-box',
              background: v.heatBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...mono,
              fontSize: 9.5,
              color: v.heatFg
            }}
          >
            {v.selected && (
              <span
                style={{
                  position: 'absolute',
                  inset: -3,
                  borderRadius: 'var(--r)',
                  border: '2px solid var(--acc)'
                }}
              />
            )}
            {h.short}
          </button>
        );
      })}
    </div>
  );
}
